<template>
  <div class="mb-10 flex size-full flex-col items-center text-gray-700 md:px-5 lg:px-10 xl:max-w-7xl">
    <div class="mb-3 flex w-full items-center px-2 md:px-0">
      <p class="flex flex-col self-start font-serif text-xl text-gray-700 md:text-2xl lg:text-3xl">
        <span>Facturas por despachos</span>
      </p>
    </div>
    <div class="flex size-full flex-col rounded-md bg-white px-4 py-5 md:px-10 md:py-5">
      <template v-if="!selectedFreeShippingInvoice">
        <p class="mb-3 text-base md:text-lg">Detalles</p>
        <lokal-loading
          v-if="freeShippingInvoicesIsLoading"
          :loading="freeShippingInvoicesIsLoading"
        />
        <div class="overflow-x-auto">
          <table class="min-w-full rounded-lg border border-gray-200 bg-white text-gray-500 shadow-sm">
            <thead class="bg-gray-50 text-center text-xs uppercase md:text-sm">
              <tr>
                <th class="border-b border-gray-200 px-6 py-3 font-medium tracking-wider">Mes / Año</th>
                <th class="border-b border-gray-200 px-6 py-3 font-medium tracking-wider">Monto total</th>
                <th class="border-b border-gray-200 px-6 py-3 font-medium tracking-wider">Factura</th>
                <th class="border-b border-gray-200 px-6 py-3 font-medium tracking-wider">Estado de pago</th>
                <th class="border-b border-gray-200 px-6 py-3 font-medium tracking-wider">Excel</th>
                <th class="border-b border-gray-200 px-6 py-3 font-medium tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 bg-white text-xs md:text-sm">
              <template
                v-for="freeShippingInvoice in orderedFreeShippingInvoices"
                :key="`free-shipping-invoices-${freeShippingInvoice.id}`"
              >
                <tr class="text-center transition-colors duration-150 hover:bg-gray-50">
                  <td class="whitespace-nowrap px-6 py-4 font-medium">
                    {{ freeShippingInvoice.month }} /
                    {{ freeShippingInvoice.year }}
                  </td>
                  <td class="whitespace-nowrap px-6 py-4">
                    {{ useFormatCurrency(freeShippingInvoice.invoiceTotalAmountCents) }}
                  </td>
                  <td class="whitespace-nowrap px-6 py-4">
                    <a
                      v-if="!!freeShippingInvoice.invoiceUrl"
                      :href="freeShippingInvoice.invoiceUrl"
                      target="_blank"
                      class="hover:underline"
                    >
                      Factura #{{ freeShippingInvoice.invoiceNumber }}
                    </a>
                  </td>
                  <td class="px-2 py-4 md:px-4 lg:px-6">
                    <span
                      class="rounded-full px-2 py-1 uppercase"
                      :class="{
                        'bg-red-100 text-red-800': freeShippingInvoice.paymentStatus === 'unpaid',
                        'bg-green-100 text-green-800': freeShippingInvoice.paymentStatus === 'paid',
                      }"
                    >
                      {{ t(`freeShippingInvoice.paymentStatus.${freeShippingInvoice.paymentStatus}`) }}
                    </span>
                  </td>
                  <td class="px-6 py-4 hover:underline">
                    <downloadable-files-form
                      :action="`/makers/${props.maker.id}/free_shipping_excel?${queryParam(freeShippingInvoice)}`"
                      label="Descargar"
                      class="hover:underline"
                    />
                  </td>
                  <td class="px-6 py-4">
                    <button
                      class="hover:underline"
                      @click="(selectedFreeShippingInvoice = freeShippingInvoice)"
                    >
                      Ver neteo
                    </button>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </template>
      <maker-shipping-invoice-show
        v-if="selectedFreeShippingInvoice"
        :free-shipping-invoice="selectedFreeShippingInvoice"
        @go-back="(selectedFreeShippingInvoice = null)"
      />
    </div>
  </div>
</template>
<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useQuery } from 'vue-query';
import { orderBy } from 'lodash';
import freeShippingInvoicesApi from 'api/free-shipping-invoices';
import { useI18n } from 'vue-i18n';
import downloadableFilesForm from './shared/downloadable-files-form.vue';
import useFormatCurrency from './use/format-currency';
import makerShippingInvoiceShow from './maker-shipping-invoice-show.vue';

const props = defineProps<{
  maker: Maker;
}>();

const { t } = useI18n();

const selectedFreeShippingInvoice = ref<FreeShippingInvoice | null>(null);

function queryParam(freeShippingInvoice: FreeShippingInvoice) {
  return `month=${freeShippingInvoice.month}&year=${freeShippingInvoice.year}`;
}

const freeShippingInvoicesQuery = useQuery(['freeShippingInvoices', props.maker.id], () =>
  freeShippingInvoicesApi.get(props.maker.id),
);
const freeShippingInvoicesIsLoading = computed(() => freeShippingInvoicesQuery.isLoading.value);
const freeShippingInvoices = computed(
  () => freeShippingInvoicesQuery.data.value?.data.freeShippingInvoices as FreeShippingInvoice[],
);
const orderedFreeShippingInvoices = computed(() =>
  orderBy(freeShippingInvoices.value, ['year', 'month'], ['desc', 'desc']),
);
</script>
